import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { guidePages, guideSections } from '@/lib/guide-structure';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';
import { useQuery } from '@tanstack/react-query';
import { getGuideBonusesData } from '@/lib/bonuses-client';

export const Route = createFileRoute('/wiki/$sectionId')({
  component: WikiSectionPage,
});

function WikiSectionPage() {
  const { sectionId } = Route.useParams();

  const section = guideSections.find(item => item.id === sectionId);
  if (!section) {
    throw notFound();
  }

  const pages = guidePages.filter(page => page.sectionId === section.id);

  const bonusesQuery = useQuery({
    queryKey: ['guide-bonuses', section.id],
    queryFn: getGuideBonusesData,
    enabled: section.id === 'reference',
    staleTime: 1000 * 60 * 10,
  });

  const toc = [
    { id: 'section-overview', label: 'Overview' },
    { id: 'pages', label: 'Pages' },
    ...(section.id === 'reference' ? [{ id: 'age-bonuses', label: 'Age Bonuses' }] : []),
  ];

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="section-overview" title={section.title}>
        <p>{section.description}</p>
        <div className="flex flex-wrap gap-2">
          <Badge>Wiki-linked</Badge>
          <Badge>Mobile-ready</Badge>
          <Badge>Expanding reference</Badge>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="pages" title="Pages in This Section">
        <div className="grid gap-3 sm:grid-cols-2">
          {pages.map(page => (
            <Card key={page.path} className="p-4">
              <h3 className="font-cinzel text-lg text-[#f0f0f0]">{page.title}</h3>
              <p className="mt-1 text-sm text-[#b0b0b0]">{page.description}</p>
              <Link
                to={page.path}
                className="mt-3 inline-flex rounded-lg border border-[#d4af37]/30 px-3 py-1.5 text-sm text-[#e6c547] hover:bg-[#d4af37]/12"
              >
                Open page
              </Link>
            </Card>
          ))}
        </div>
      </WikiAnchorSection>

      {section.id === 'reference' ? (
        <WikiAnchorSection id="age-bonuses" title="Age Bonuses (Live)">
          {bonusesQuery.isLoading ? <p>Loading current age bonus data...</p> : null}
          {bonusesQuery.isError ? (
            <p>
              Bonus data could not be loaded right now. The guide still works, but this section needs the
              published Storage bundle manifests.
            </p>
          ) : null}
          {bonusesQuery.data ? (
            <div className="space-y-3">
              <p>
                Source: <strong>{bonusesQuery.data.source}</strong> | Age: <strong>{bonusesQuery.data.ageName}</strong>
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Card className="p-4">
                  <h3 className="font-cinzel text-lg text-[#f0f0f0]">Races</h3>
                  <p className="text-sm text-[#b0b0b0]">{bonusesQuery.data.races.length} entries</p>
                </Card>
                <Card className="p-4">
                  <h3 className="font-cinzel text-lg text-[#f0f0f0]">Personalities</h3>
                  <p className="text-sm text-[#b0b0b0]">{bonusesQuery.data.personalities.length} entries</p>
                </Card>
                <Card className="p-4">
                  <h3 className="font-cinzel text-lg text-[#f0f0f0]">Advisors</h3>
                  <p className="text-sm text-[#b0b0b0]">{bonusesQuery.data.advisors.length} entries</p>
                </Card>
                <Card className="p-4">
                  <h3 className="font-cinzel text-lg text-[#f0f0f0]">Items</h3>
                  <p className="text-sm text-[#b0b0b0]">{bonusesQuery.data.items.length} entries</p>
                </Card>
              </div>
              <p className="text-xs text-[#9d9d9d]">
                This guide intentionally exposes high-level effect summaries and avoids exact formula logic.
              </p>
            </div>
          ) : null}
        </WikiAnchorSection>
      ) : null}
    </div>
  );
}
